/**
 * R232q51: client-side weapon mesh attachment.
 *
 * Loads a Sirus item GLB from `/aiwari/models/sirus/Item/ObjectComponents/weapon/`
 * (deployed pool of ~25 weapons + ~14k head-slot helmets) and attaches it
 * to the rig's right_hand bone (mainhand) or left_hand bone (offhand).
 *
 * Mirrors the RealisticCharacter master-AssetContainer cache pattern so the
 * same itemId is fetched once per session and instantiated as needed.
 *
 * NOTE: This module is purely additive. RealisticCharacter.ts retains its
 * existing manifest-based weapon resolution + procedural fallback, and
 * delegates to attachWeaponToBone() FIRST when a Sirus weapon GLB exists
 * for the requested itemId. If the GLB is absent (404), this module returns
 * null and the caller falls through to its legacy chain.
 */

import {
  type AbstractMesh,
  type AssetContainer,
  type Bone,
  type Mesh,
  type Node,
  type Scene,
  type Skeleton,
  Quaternion,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { sanitizeGlb } from './glb-sanitizer.js';

// R232q86zb9: deployed symlinks live at the CapitalCase path
// `/aiwari/models/sirus/Item/ObjectComponents/weapon/`. The lowercase variant
// hits nginx SPA fallback (text/html 200), which `urlExists` correctly
// rejects — but then we fall back to the generic CC0 sword for every itemId
// instead of the proper Sirus weapon. Case must match the on-disk dir.
const SIRUS_WEAPON_BASE = `${import.meta.env.BASE_URL}models/sirus/Item/ObjectComponents/weapon/`;
/** Generic CC0 sword fallback shipped under public/models/wow-vanilla/weapons/all/ */
const GENERIC_SWORD_URL = `${import.meta.env.BASE_URL}models/wow-vanilla/weapons/all/long-sword.glb`;

/** Per-URL master AssetContainer cache (mirrors RealisticCharacter pattern). */
const WEAPON_MASTER_CACHE = new Map<string, Promise<AssetContainer | null>>();
/** HEAD-probe result cache so we don't hammer the CDN with the same 404 in
 *  console for every equip-toggle. true = exists, false = 404, undefined = unprobed. */
const URL_EXISTS_CACHE = new Map<string, boolean>();

/** Slot for which a weapon mesh attachment is supported. */
export type WeaponAttachSlot = 'mainhand' | 'offhand';

/** Tracks the currently attached TransformNode per (rig, slot) so a follow-up
 *  call to attachWeaponToBone disposes the previous mesh before placing the
 *  new one. Keyed on the rig's importRoot id (caller passes the rig object). */
interface AttachState {
  byRig: WeakMap<object, Map<WeaponAttachSlot, TransformNode>>;
}
const ATTACH_STATE: AttachState = { byRig: new WeakMap() };

/** Minimal rig shape this module needs. Compatible with RealisticRig + others. */
export interface AttachableRig {
  /** Rig root or any node that contains skeleton-bearing meshes. */
  root: TransformNode;
  /** Optional explicit skeleton (if caller has it on hand). */
  skeleton?: Skeleton | null;
  /** Optional inner node used as a non-bone parent fallback. */
  inner?: TransformNode | null;
}

/** HEAD-probe a URL; cached. nginx SPA fallbacks (text/html with 200) are
 *  treated as "missing" so we don't try to parse index.html as GLB. */
async function urlExists(url: string): Promise<boolean> {
  const cached = URL_EXISTS_CACHE.get(url);
  if (cached !== undefined) return cached;
  let exists = false;
  try {
    const res = await fetch(url, { method: 'HEAD' });
    if (res.ok) {
      const ct = (res.headers.get('content-type') ?? '').toLowerCase();
      exists = !ct.includes('text/html');
    }
  } catch {
    exists = false;
  }
  URL_EXISTS_CACHE.set(url, exists);
  return exists;
}

/** Lazy-load + cache the master AssetContainer for a weapon GLB URL. */
async function getWeaponMaster(scene: Scene, url: string): Promise<AssetContainer | null> {
  let p = WEAPON_MASTER_CACHE.get(url);
  if (p) return p;
  p = (async () => {
    let blobUrl: string | null = null;
    try {
      const sanitised = await sanitizeGlb(url);
      blobUrl = sanitised.blobUrl;
      const { LoadAssetContainerAsync } = await import('@babylonjs/core/Loading/sceneLoader.js');
      const container = await LoadAssetContainerAsync(blobUrl, scene, { pluginExtension: '.glb' });
      return container;
    } catch (err) {
      const e = err as Error;
      // Silent fallback path — the caller will try the generic sword next.
      console.warn(`[ItemMeshAttach] weapon GLB parse failed: ${url} (${e?.message ?? 'unknown'})`);
      return null;
    } finally {
      if (blobUrl) {
        const urlToRevoke = blobUrl;
        let frameCount = 0;
        const obs = scene.onAfterRenderObservable.add(() => {
          frameCount++;
          if (frameCount >= 3) {
            scene.onAfterRenderObservable.remove(obs);
            try { URL.revokeObjectURL(urlToRevoke); } catch { /* nop */ }
          }
        });
        void obs;
      }
    }
  })();
  WEAPON_MASTER_CACHE.set(url, p);
  return p;
}

/** Dispose every cached master container (call on scene teardown). */
export function clearWeaponMeshCache(): void {
  for (const p of WEAPON_MASTER_CACHE.values()) {
    p.then((c) => {
      if (c) {
        try { c.dispose(); } catch { /* already gone */ }
      }
    }).catch(() => {});
  }
  WEAPON_MASTER_CACHE.clear();
  URL_EXISTS_CACHE.clear();
}

/** Walk a rig to find a skeleton if not provided. Picks the first non-empty
 *  Skeleton attached to any AbstractMesh under the rig root. */
function resolveSkeleton(rig: AttachableRig): Skeleton | null {
  if (rig.skeleton) return rig.skeleton;
  const stack: Node[] = [rig.root];
  while (stack.length) {
    const n = stack.pop()!;
    const maybe = n as unknown as { skeleton?: Skeleton | null };
    if (maybe.skeleton && maybe.skeleton.bones && maybe.skeleton.bones.length > 0) {
      return maybe.skeleton;
    }
    for (const ch of n.getChildren()) stack.push(ch);
  }
  return null;
}

/** Bone-name patterns Sirus / Quaternius / Mixamo / KayKit rigs use. */
const HAND_R_PATTERNS: RegExp[] = [
  /^hand_r$/i, /^r_hand$/i, /^righthand$/i, /right_?hand/i,
  /Bip01.*R.*Hand/i, /mixamorig:RightHand/i, /handslot\.r/i, /Fist\.R/i,
  /^bone_25$/i, // Sirus M2 default rig — bone index 25 is right hand
];
const HAND_L_PATTERNS: RegExp[] = [
  /^hand_l$/i, /^l_hand$/i, /^lefthand$/i, /left_?hand/i,
  /Bip01.*L.*Hand/i, /mixamorig:LeftHand/i, /handslot\.l/i, /Fist\.L/i,
  /^bone_42$/i, // Sirus M2 default rig — bone index 42 is left hand (heuristic)
];

function findBone(skeleton: Skeleton, patterns: RegExp[]): Bone | null {
  for (const p of patterns) {
    const b = skeleton.bones.find((bn) => p.test(bn.name));
    if (b) return b;
  }
  return null;
}

/** Dispose and remove the previous attachment for a (rig, slot) pair. */
function detachExisting(rig: AttachableRig, slot: WeaponAttachSlot): void {
  const slotMap = ATTACH_STATE.byRig.get(rig);
  if (!slotMap) return;
  const prev = slotMap.get(slot);
  if (prev) {
    try { prev.dispose(false, true); } catch { /* already gone */ }
    slotMap.delete(slot);
  }
}

/** Build the candidate URL list for an itemId. Sirus-extracted GLB first,
 *  generic CC0 sword last. */
function candidateUrls(itemId: string): string[] {
  const safeId = itemId.replace(/[^a-zA-Z0-9._-]/g, '');
  if (!safeId) return [GENERIC_SWORD_URL];
  return [
    `${SIRUS_WEAPON_BASE}${safeId}.glb`,
    GENERIC_SWORD_URL,
  ];
}

/**
 * Attach a weapon mesh to the appropriate hand bone of the rig.
 *
 * Returns the attachment TransformNode on success, or null if no candidate
 * GLB could be loaded (caller may then fall back to procedural geometry).
 *
 * Behaviour:
 *  - HEAD-probes each candidate URL before parsing (no noisy 404 spam).
 *  - Master-caches the parsed AssetContainer per URL (1 fetch per itemId).
 *  - Disposes any previously-attached mesh on the same (rig, slot) pair.
 *  - Resolves bone via skeleton-name patterns; falls back to rig.inner /
 *    rig.root + an offset when no hand bone is found.
 *  - Never throws — silent return on missing GLB / parse failure.
 */
export async function attachWeaponToBone(
  rig: AttachableRig,
  slot: WeaponAttachSlot,
  itemId: string,
): Promise<TransformNode | null> {
  // Always detach previous mesh first (slot change semantics).
  detachExisting(rig, slot);
  if (!itemId) return null;

  const scene = rig.root.getScene();
  const urls = candidateUrls(itemId);

  let chosenUrl: string | null = null;
  let container: AssetContainer | null = null;
  for (const url of urls) {
    if (!(await urlExists(url))) continue;
    container = await getWeaponMaster(scene, url);
    if (container) { chosenUrl = url; break; }
  }
  if (!container || !chosenUrl) return null;

  // Instantiate. Weapons are static (no skin) so cloneMaterials=true is fine
  // — keeps per-instance tint tracking sane.
  const inst = container.instantiateModelsToScene((n) => n, true);
  const collected: AbstractMesh[] = [];
  const stack: Node[] = [...inst.rootNodes];
  while (stack.length) {
    const n = stack.pop()!;
    const maybeMesh = n as unknown as { getTotalVertices?: () => number };
    if (typeof maybeMesh.getTotalVertices === 'function' && (maybeMesh.getTotalVertices?.() ?? 0) > 0) {
      collected.push(n as unknown as AbstractMesh);
    }
    for (const ch of n.getChildren()) stack.push(ch);
  }
  if (collected.length === 0) {
    try { inst.dispose(); } catch { /* nop */ }
    return null;
  }

  // Resolve the target bone.
  const skeleton = resolveSkeleton(rig);
  const bone = skeleton
    ? findBone(skeleton, slot === 'mainhand' ? HAND_R_PATTERNS : HAND_L_PATTERNS)
    : null;

  // Build the attachment TransformNode.
  const attachRoot = new TransformNode(`weapon-${slot}-${itemId}`, scene);
  if (bone) {
    const tn = bone.getTransformNode();
    if (tn) attachRoot.parent = tn;
    else attachRoot.parent = rig.inner ?? rig.root;
  } else {
    // No hand bone — pin to inner/root with a forward+up offset so the
    // weapon is at least visible (better than nothing per task spec).
    attachRoot.parent = rig.inner ?? rig.root;
    attachRoot.position = new Vector3(0.30, 1.10, 0.10);
  }
  // Standard hand-grip orientation: 90° pitch so the blade points away
  // from the palm.
  attachRoot.rotationQuaternion = Quaternion.RotationYawPitchRoll(0, Math.PI / 2, 0);
  attachRoot.scaling = new Vector3(0.7, 0.7, 0.7);

  // Parent every leaf mesh to attachRoot. Dispose any skeletons the GLB
  // shipped with — weapons render as static geometry on a bone.
  for (const m of collected) {
    (m as Mesh).parent = attachRoot;
    (m as Mesh).isPickable = false;
  }
  for (const sk of inst.skeletons) {
    try { sk.dispose(); } catch { /* nop */ }
  }
  // Free the instantiated animation groups too — weapons don't animate
  // independently from the rig.
  for (const ag of inst.animationGroups) {
    try { ag.dispose(); } catch { /* nop */ }
  }

  // Track for next-call dispose.
  let slotMap = ATTACH_STATE.byRig.get(rig);
  if (!slotMap) {
    slotMap = new Map();
    ATTACH_STATE.byRig.set(rig, slotMap);
  }
  slotMap.set(slot, attachRoot);

  return attachRoot;
}

/** Detach (and dispose) the weapon currently attached to (rig, slot).
 *  Useful when the caller wants to clear the slot without loading anything new. */
export function detachWeapon(rig: AttachableRig, slot: WeaponAttachSlot): void {
  detachExisting(rig, slot);
}
